import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn} from "@angular/forms";
import {ApiErrorDetail} from "../app.types";

/**
 * Loops through all form controls on a formGroup, marking them as touched and focusing on the first invalid control.
 * @param {FormGroup} form
 * @returns {boolean}
 */
export function isFormValid(form: FormGroup): boolean {
    const isValid = form.valid;

    // Touch all controls
    for (const key in form.controls) {
        const control = form.controls[key] as FormControl;
        if (control.invalid) {
            control.markAsTouched();
        }
    }

    // Focus on first invalid control
    if (isValid == false) {
        for (const key in form.controls) {
            const control = form.controls[key] as FormControl;
            if (control.invalid) {
                const element = document.querySelector("[formControlName=" + key + "]") as HTMLElement;
                if (element != null) {element.focus();}
                break;
            }
        }
    }
    return isValid;
}

/**
 * Clears every control an API error points at, so the user is not left holding a value the server
 * has already rejected. Controls are matched on the error detail's target, which the server sets to
 * the name of the offending field.
 * @param {FormGroup} form
 * @param {ApiErrorDetail[]} details
 * @returns {boolean} whether any control was cleared
 */
export function clearControlsForErrorDetails(form: FormGroup, details: ApiErrorDetail[]): boolean {
    const targeted = (details ?? [])
        .map(detail => findTargetedControl(form, detail.target))
        .filter(control => control != null);

    targeted.forEach(control => {
        control.reset("");
        control.markAsTouched();
    });

    return targeted.length > 0;
}

/**
 * Finds the control an error detail names. The server names the field as its API model spells it, so
 * a control whose name only differs in casing still counts as a match.
 * @param {FormGroup} form
 * @param {string} target
 * @returns {AbstractControl} the control, or null when the form has no such field
 */
function findTargetedControl(form: FormGroup, target: string): AbstractControl {
    if (!target) {return null;}

    const control = form.get(target);
    if (control != null) {return control;}

    const name = Object.keys(form.controls).find(x => x.toLowerCase() === target.toLowerCase());

    return name == null ? null : form.controls[name];
}

/**
 * Checks that at least one of the controls on a formGroup fulfils the passed in validator.
 * @param {ValidatorFn} validator
 * @returns {(group: FormGroup) => (ValidationErrors | null)}
 */
export const atLeastOne = (validator: ValidatorFn) => (
    group: FormGroup,
): ValidationErrors | null => {
    const hasAtLeastOne = group && group.controls && Object.keys(group.controls)
        .some(k => !validator(group.controls[k]));

    return hasAtLeastOne ? null : {
        atLeastOne: true,
    };
};